"use client";

// WO-GF-001 B4.2 — the interactive schedule-row map. 1:1 behavior port of legacy `MountainSeo/Buses.js`:
// each bus-operator row lists its pickup/drop-off stops as links; clicking any stop link toggles a map of
// that operator's stops (single-open — opening one closes the previous), exactly as the legacy `BusRow`
// `setMapId` toggle did. Document-scoped hydrator (MapLayer family): it wires the CAPTURED mirror table
// (the map was click-gated so never captured — R6-6 "dead interaction") to the real PortMap. The maps are
// hidden by default, so the pixel gate is untouched; the behavior run proves the toggle.

import { useEffect } from "react";
import { createRoot, type Root } from "react-dom/client";
import PortMap, { type PortMapMarker } from "./PortMap";

// Match MapLayer / harness NORMALIZE_HEIGHTS.
const MAP_REGION_HEIGHT = 320;

export default function ScheduleMapLayer() {
  useEffect(() => {
    const rows = Array.from(document.querySelectorAll<HTMLElement>('[class*="colsRow"]'));
    if (!rows.length) return;

    type Entry = { wrap: HTMLElement; root: Root; markers: PortMapMarker[]; operator: string; rendered: boolean };
    const entries: Entry[] = [];
    const cleanups: Array<() => void> = [];
    let openIdx: number | null = null;

    rows.forEach((row) => {
      const operator = row.querySelector("h3")?.textContent?.trim() || "";
      // Stop links live in the pickup + drop-off spans as <a href="javascript:void(0);">NAME</a>.
      const links = Array.from(row.querySelectorAll<HTMLAnchorElement>('a[href^="javascript"]'));
      const stopNames = links.map((a) => (a.textContent || "").trim()).filter(Boolean);
      if (!stopNames.length) return;
      // FOUNDER MAP RULING 2026-08-27 — "do not use transport-stops.stub anywhere visitor-facing."
      // These per-operator toggles drew their pins from `resolveStops`, the stub, which is precisely
      // what the ruling forbids on a customer surface. The toggle, the single-open behaviour and the
      // legacy parity are all kept; what is withdrawn is the invented geography. With no marker the
      // panel says so — see the empty-state below — instead of pinning a stop where it may not be.
      //
      // The authoritative replacement is named rather than left as a gap: the engine's own
      // `stopDetails[].location`, which arrives per ROUTE and DATE. This row has an operator and a
      // stop NAME and neither a route nor a date, so it cannot ask for it here; the results screen
      // can and does. Restore pins from that source when this row can name a route and date.
      const markers: PortMapMarker[] = [];

      // Toggle container inserted right after the row (legacy `border-bottom-class-bus-row-container`).
      const wrap = document.createElement("div");
      wrap.setAttribute("data-schedule-map", "");
      wrap.setAttribute("data-operator", operator);
      wrap.style.cssText = `display:none;height:${MAP_REGION_HEIGHT}px;min-height:${MAP_REGION_HEIGHT}px;overflow:hidden;margin:8px 0;`;
      row.parentElement?.insertBefore(wrap, row.nextSibling);
      const host = document.createElement("div");
      host.style.cssText = "width:100%;height:100%;";
      wrap.appendChild(host);

      const idx = entries.length;
      const entry: Entry = { wrap, root: createRoot(host), markers, operator, rendered: false };
      entries.push(entry);

      const toggle = (e: Event) => {
        e.preventDefault();
        if (openIdx === idx) {
          wrap.style.display = "none";
          openIdx = null;
          return;
        }
        const prevOpen = openIdx !== null ? entries[openIdx] : undefined;
        if (prevOpen) prevOpen.wrap.style.display = "none";
        if (!entry.rendered) {
          entry.root.render(
            markers.length === 0 ? (
              // FOUNDER MAP RULING 2026-08-27 — honest no-map copy, never guessed geography. The
              // stops themselves are still listed in the row above this panel; what is missing is
              // where they are, and saying so is the whole point.
              <div
                data-schedule-map-nomap=""
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  padding: "0 24px",
                  fontSize: 13,
                  lineHeight: 1.5,
                  color: "#5b6b76",
                  background: "#eef2f4",
                }}
              >
                {`Stop locations for ${operator || "this operator"} are not published as coordinates, so there is no map to show. The stops are listed above.`}
              </div>
            ) : (
            <PortMap
              markers={markers}
              mode="default"
              surface={`schedule-${operator || "operator"}`}
              rightTopIcon={
                operator ? (
                  <span
                    style={{
                      background: "#fff",
                      border: "1px solid #e3e6ef",
                      borderRadius: 6,
                      padding: "2px 8px",
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#162e4d",
                    }}
                  >
                    {operator}
                  </span>
                ) : undefined
              }
            />
            ),
          );
          entry.rendered = true;
        }
        wrap.style.display = "";
        openIdx = idx;
      };

      for (const a of links) {
        a.style.cursor = "pointer";
        a.addEventListener("click", toggle);
        cleanups.push(() => a.removeEventListener("click", toggle));
      }
    });

    return () => {
      for (const fn of cleanups) fn();
      for (const e of entries) {
        const r = e.root;
        queueMicrotask(() => r.unmount());
        e.wrap.remove();
      }
    };
  }, []);

  return null;
}
